/* useCurrentCall — what the agent-desk surface should be rendering RIGHT NOW.
 *
 * Tri-state model so the UI can stay populated through wrap-up:
 *
 *   idle    No call relevant. Show empty placeholders.
 *   active  Call in progress (calls.ended_at IS NULL). Live data; panes
 *           populate.
 *   wrapup  Call just ended (within WRAPUP_WINDOW_MS). Panes stay
 *           populated so the agent can finish notes, the header shows
 *           "ended Xs ago" and a Done button. Tick by 1s so the
 *           "ended Xs ago" counter updates live.
 *
 * Dismissal is client-side only — call dismiss() to flip wrap-up → idle.
 * We remember which call_id was dismissed so a fresh call still triggers
 * a fresh wrap-up cycle. No API call needed; the agent's local state
 * is the source of truth for "I'm done with this one."
 *
 * Polling: 2s during idle/wrap-up (cheap), same during active. Could
 * be replaced by an SSE "any new open call?" channel later. */

import { useCallback, useEffect, useRef, useState } from "react";

import { API_URL } from "../lib/api";

const POLL_INTERVAL_MS = 2_000;
// Calls older than this and still ended_at=null are treated as orphan
// rows (e.g. /ws-listen crash that never finalised). Don't render them
// as "active" or we'd hijack the agent's screen with a ghost call.
const ACTIVE_MAX_AGE_MS = 5 * 60_000;
// Window after end_at during which we keep showing the call as wrap-up.
// 30 min — long enough that an agent can step away briefly and still
// come back to context; a fresh inbound call auto-takes the slot before
// then anyway, so the timer is really a "you walked away for the day"
// safety net, not the primary clear path.
const WRAPUP_WINDOW_MS = 30 * 60_000;
// Re-tick cadence for the "ended Xs ago" counter so the header refreshes
// live without re-fetching from the API.
const TICK_INTERVAL_MS = 1_000;

export type CallStatus = "idle" | "active" | "wrapup";

export type CurrentCall = {
  id: number | undefined;
  status: CallStatus;
  /** Call.started_at — drives "duration" in the active header. */
  startedAt: Date | null;
  /** Call.ended_at — null when active. */
  endedAt: Date | null;
  /** Seconds since end (null when active). Updates every TICK_INTERVAL_MS. */
  endedAgoSecs: number | null;
  /** Seconds since start (null when idle). Updates every TICK_INTERVAL_MS. */
  durationSecs: number | null;
  /** Caller's phone — useful when customer_id is null (unknown caller). */
  fromNumber: string | null;
  /** Mark the current wrap-up as done — flips status to idle until next call. */
  dismiss: () => void;
};

type CallRow = {
  id: number;
  ended_at: string | null;
  started_at: string;
  from_number: string | null;
  // Server-derived: 'live' (recent activity), 'orphaned' (ended_at IS
  // NULL but no log activity in 60s — bot died mid-call), 'ended'
  // (cleanly finalised). Optional because older API builds didn't
  // expose it; we treat missing as a hint to fall back on ended_at.
  activity_status?: "live" | "orphaned" | "ended";
  // Agent-desk Done timestamp (ISO). When set, the wrap-up hook skips
  // this row — the agent already finished post-call and we should not
  // resurrect the wrap-up after a refresh or on a sibling device.
  // Optional for compat with older API builds (treated as not-dismissed).
  agent_dismissed_at?: string | null;
};

const IDLE: CurrentCall = {
  id: undefined,
  status: "idle",
  startedAt: null,
  endedAt: null,
  endedAgoSecs: null,
  durationSecs: null,
  fromNumber: null,
  dismiss: () => {},
};

export function useCurrentCall(): CurrentCall {
  const [row, setRow] = useState<CallRow | null>(null);
  const [now, setNow] = useState<number>(Date.now());
  const dismissedIdRef = useRef<number | null>(null);

  // Poll API for the most recent call.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function poll() {
      try {
        const r = await fetch(`${API_URL}/api/calls?limit=5`);
        if (!r.ok) return;
        const d = (await r.json()) as { calls: CallRow[] };
        const next = d.calls?.[0] ?? null;
        if (!cancelled) setRow(next);
      } catch {
        /* keep last value through transient blips */
      } finally {
        if (!cancelled) timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    }
    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  // 1s tick so "duration" / "ended Xs ago" counters update without
  // hitting the API again.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const dismiss = useCallback(() => {
    if (row?.id === undefined) return;
    const id = row.id;
    // Optimistic local flip — keeps the UI responsive even if the
    // server PATCH is slow or fails. The server-side flag is what
    // makes refresh / cross-device persistence work; the ref is what
    // makes the UI flip in this tab right now.
    dismissedIdRef.current = id;
    setNow(Date.now());
    // Fire-and-forget. If the PATCH fails (transient network blip),
    // the next refresh will re-show the wrap-up — agent can hit Done
    // again. We intentionally don't surface a toast or block the UI
    // here; Done is a "I'm finished" affordance, not a save action.
    void fetch(`${API_URL}/api/calls/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ agent_dismissed_at: new Date().toISOString() }),
    }).catch(() => {});
  }, [row]);

  if (!row) return { ...IDLE, dismiss };

  const startedAt = parseSqliteTimestamp(row.started_at);
  const endedAt = row.ended_at ? parseSqliteTimestamp(row.ended_at) : null;
  const ageMs = startedAt ? now - startedAt.getTime() : Number.POSITIVE_INFINITY;
  const endedAgoMs = endedAt ? now - endedAt.getTime() : null;

  // ── Active: open call with fresh activity. Prefer the server's
  // activity_status (computed from call_logs.ts) when available; fall
  // back to "ended_at IS NULL AND started < 5min ago" so older API
  // builds keep working. Orphans (status === 'orphaned') skip this
  // branch entirely so the page doesn't get hijacked by a dead row.
  const isLive =
    row.activity_status === "live" ||
    (row.activity_status === undefined &&
      endedAt === null &&
      ageMs < ACTIVE_MAX_AGE_MS);
  if (isLive) {
    return {
      id: row.id,
      status: "active",
      startedAt,
      endedAt: null,
      endedAgoSecs: null,
      durationSecs: startedAt ? Math.max(0, Math.floor(ageMs / 1000)) : null,
      fromNumber: row.from_number,
      dismiss,
    };
  }

  // ── Wrap-up: call ended within WRAPUP_WINDOW_MS and neither the
  // server nor this tab has marked it dismissed. The server-side check
  // (agent_dismissed_at IS NOT NULL) is what makes refresh and
  // cross-device dismissal stick; the ref is the optimistic in-tab
  // flip from the most recent dismiss() click. A fresh call (different
  // id) replaces `row` here and naturally takes over the slot.
  if (
    endedAt !== null &&
    endedAgoMs !== null &&
    endedAgoMs < WRAPUP_WINDOW_MS &&
    dismissedIdRef.current !== row.id &&
    !row.agent_dismissed_at
  ) {
    return {
      id: row.id,
      status: "wrapup",
      startedAt,
      endedAt,
      endedAgoSecs: Math.max(0, Math.floor(endedAgoMs / 1000)),
      durationSecs:
        startedAt && endedAt
          ? Math.max(0, Math.floor((endedAt.getTime() - startedAt.getTime()) / 1000))
          : null,
      fromNumber: row.from_number,
      dismiss,
    };
  }

  return { ...IDLE, dismiss };
}

// SQLite `datetime('now')` writes "YYYY-MM-DD HH:MM:SS" without a
// timezone suffix — Safari's Date parser treats those as local time,
// which is wrong (they're UTC). Force-parse as UTC.
function parseSqliteTimestamp(s: string): Date | null {
  if (!s) return null;
  // Already ISO-like (e.g. seeded data) — leave Date to handle it.
  if (s.includes("T")) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s.replace(" ", "T") + "Z");
  return Number.isNaN(d.getTime()) ? null : d;
}
